// ==============================================================================
// UTILITÁRIO CRIPTOGRÁFICO DE INTEGRIDADE (SHA-256 REAL)
// Em conformidade com a Lei Federal nº 14.063/2020 (Assinatura Eletrônica)
// ==============================================================================

import { getLocalDateString } from './calendarUtils';

/**
 * Implementação pura em JS do algoritmo SHA-256 (FIPS PUB 180-4)
 * Garante hash criptográfico de 256 bits mesmo em ambientes sem Web Crypto API
 */
function pureJsSha256(ascii: string): string {
  function rightRotate(value: number, amount: number) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  const lengthProperty = 'length';
  let i = 0;
  let j = 0;
  let result = '';

  const words: number[] = [];
  const asciiBitLength = ascii[lengthProperty] * 8;

  const hash: number[] = [];
  const k: number[] = [];
  let primeCounter = 0;

  const isPrime = (n: number) => {
    for (let factor = 2; factor * factor <= n; factor++) {
      if (n % factor === 0) return false;
    }
    return true;
  };

  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (isPrime(candidate)) {
      if (primeCounter < 8) {
        hash[primeCounter] = (mathPow(candidate, 1 / 2) * maxWord) | 0;
      }
      k[primeCounter] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
      primeCounter++;
    }
  }

  ascii += '\x80';
  while ((ascii[lengthProperty] % 64) - 56) ascii += '\x00';
  for (i = 0; i < ascii[lengthProperty]; i++) {
    j = ascii.charCodeAt(i);
    if (j >> 8) return ''; // Apenas caracteres 8-bit
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words[lengthProperty]] = (asciiBitLength / maxWord) | 0;
  words[words[lengthProperty]] = asciiBitLength;

  for (j = 0; j < words[lengthProperty];) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0);

    for (i = 0; i < 64; i++) {
      const i2 = i + j;
      const w15 = w[i - 15];
      const w2 = w[i - 2];

      const s0 = rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3);
      const s1 = rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10);
      w[i] =
        i < 16
          ? w[i]
          : (w[i - 16] + s0 + w[i - 7] + s1) | 0;

      const s1Main =
        rightRotate(hash[4], 6) ^ rightRotate(hash[4], 11) ^ rightRotate(hash[4], 25);
      const ch = (hash[4] & hash[5]) ^ (~hash[4] & hash[6]);
      const temp1 = (hash[7] + s1Main + ch + k[i] + w[i]) | 0;
      const s0Main =
        rightRotate(hash[0], 2) ^ rightRotate(hash[0], 13) ^ rightRotate(hash[0], 22);
      const maj =
        (hash[0] & hash[1]) ^ (hash[0] & hash[2]) ^ (hash[1] & hash[2]);
      const temp2 = (s0Main + maj) | 0;

      hash[7] = hash[6];
      hash[6] = hash[5];
      hash[5] = hash[4];
      hash[4] = (hash[3] + temp1) | 0;
      hash[3] = hash[2];
      hash[2] = hash[1];
      hash[1] = hash[0];
      hash[0] = (temp1 + temp2) | 0;
    }

    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (let bit = 3; bit >= 0; bit--) {
      const b = (hash[i] >> (bit * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

/**
 * Calcula o hash SHA-256 real de qualquer texto
 */
export async function computeSha256Hex(text: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
      console.warn('SubtleCrypto falhou, usando algoritmo SHA-256 JS:', e);
    }
  }
  return pureJsSha256(text);
}

/**
 * Gera hash de integridade imutável para assinaturas digitais
 */
export async function generateDocumentHash(payload: {
  documentId: string;
  documentType: string;
  providerName: string;
  clientName: string;
  clientDocument?: string;
  timestampIso: string;
  signatureBase64: string;
  totalValue?: number;
}): Promise<string> {
  const canonicalString = [
    `ID:${payload.documentId}`,
    `TYPE:${payload.documentType}`,
    `PROV:${payload.providerName.trim().toUpperCase()}`,
    `CLI:${payload.clientName.trim().toUpperCase()}`,
    `DOC:${(payload.clientDocument || '').trim()}`,
    `VAL:${(payload.totalValue || 0).toFixed(2)}`,
    `TIME:${payload.timestampIso}`,
    `SIG:${payload.signatureBase64.slice(0, 100)}`
  ].join('|');

  return await computeSha256Hex(canonicalString);
}

/**
 * Gera código de verificação criptográfico para Recibos
 */
export async function generateReceiptSha256Hash(payload: {
  receiptNumber: string;
  amountPaid: number;
  clientName: string;
  issuedAt: string;
  providerDocument?: string;
  providerName?: string;
}): Promise<string> {
  const canonical = `RMAX|${payload.receiptNumber}|${payload.amountPaid.toFixed(2)}|${payload.clientName.trim().toUpperCase()}|${payload.issuedAt}|${payload.providerDocument || payload.providerName || ''}`;
  const fullHash = await computeSha256Hex(canonical);
  const upper = fullHash.toUpperCase();
  return `RMAX-REC-${upper.slice(0, 4)}-${upper.slice(4, 8)}-${upper.slice(8, 12)}`;
}

/**
 * Calcula o hash SHA-256 de forma síncrona
 */
export function computeSha256HexSync(text: string): string {
  return pureJsSha256(text);
}

/**
 * Gera hash de assinatura imutável para Prontuários Veterinários / SOAP de forma síncrona
 */
export function generateCfmvSignatureHashSync(payload: {
  consultationId: string;
  patientId?: string;
  crmv?: string;
  finalizedAt?: string;
}): string {
  const canonical = `CFMV|${payload.consultationId}|${payload.patientId || ''}|${payload.crmv || 'CRMV-SP'}|${payload.finalizedAt || ''}`;
  const hex = pureJsSha256(canonical);
  const upper = hex.toUpperCase();
  const registrationPrefix = payload.crmv ? payload.crmv.replace(/\s+/g, '-') : 'CFMV-SP';
  return `${registrationPrefix}-${upper.slice(0, 5)}-${upper.slice(5, 13)}`;
}

/**
 * Gera hash de assinatura imutável para Prontuários Veterinários / SOAP (CFMV / Lei 14.063/20)
 * Baseado no SHA-256 do identificador do prontuário, paciente e carimbo de tempo
 */
export async function generateCfmvSignatureHash(payload: {
  consultationId: string;
  patientId?: string;
  crmv?: string;
  finalizedAt: string;
}): Promise<string> {
  return generateCfmvSignatureHashSync(payload);
}

/**
 * Gera número de recibo padronizado com data civil local e entropia alfanumérica segura
 * Formato: REC-YYYYMMDD-XXXXXX
 */
export function generateReceiptNumber(date?: Date | string): string {
  const datePart = getLocalDateString(date).replace(/-/g, '');
  const entropy = (typeof crypto !== 'undefined' && crypto.randomUUID 
    ? crypto.randomUUID().replace(/-/g, '') 
    : Math.random().toString(36).slice(2)
  ).slice(0, 6).toUpperCase();
  return `REC-${datePart}-${entropy}`;
}

